import { Calendar } from "@/models/calendar/Calendar";
import { Resident } from "@/models/resident/Resident";
import { ServiceRequest } from "@/models/ServiceRequest";
import { Notification } from "@/models/Notification";
import { FacilitySettingsModel } from "@/models/FacilitySettings";
import { PremiumServiceEventTypes } from "@/enums/PremiumServiceEventTypes";
import { NewAdditionalService } from "@/models/additional-service/NewAdditionalService";
import { getFormattedDateTimeString } from "@/utils/dateTimeFormat";
import { LikeTypeEnum } from "@/enums/LikeTypeEnum";
import moment from "moment";

export default {
  name: "Dashboard",
  data() {
    return {
      likeTypeEnum: LikeTypeEnum,
      loading: true,
      todayDate: moment().toDate(),
      sevenDaysAgo: moment().startOf('day').subtract(7, 'days').toDate(),
      headerBirthdays: [
        {
          value: "imageUrl"
        },
        {
          value: "dateOfBirth"
        },
        {
          value: "shortDob"
        },
      ],
      headerServices: [
        {
          value: "serviceName",
        },
        {
          value: "residentName"
        },
      ],
      headerEvents: [
        {
          value: "name",
        },
        {
          value: "location",
          align: "end"
        },
        {
          value: "start",
          align: "end",
        }
      ],
      headerResidents: [
        {
          value: "thumbnailUrl"
        },
        {
          value: "pname"
        },
        {
          value: "attendanceCount"
        }
      ],
      headerVideocall: [
        {
          value: "name",
        },
        {
          value: "inviterName",
        },
        {
          value: "start",
          align: "end"
        }
      ],
      headerLikes: [
        {
          value: "title",
        },
        {
          value: "dateDisplay",
          align: "end"
        }
      ],
      headerNotifications: [
        {
          value: "residentId"
        },
        {
          value: "title"
        }
      ],
      headerAdHocActivities: [
        {
          value: "residentName"
        },
        {
          value: "count",
          align: "end"
        }
      ]
    }
  },
  async created() {
    try {
      await Promise.all([
        this.$store.dispatch("getFacility"),
        this.$store.dispatch("getFamilyLikes"),
        this.$store.dispatch("getNotifications"),
        this.$store.dispatch("getCalenderEventsUpcoming"),
        this.$store.dispatch("getAwaitingApprovalRequests"),
        this.$store.dispatch("getPastCalendarEvents"),
        this.$store.dispatch("getResidentWithUpcomingBirthdays"),
        this.$store.dispatch("getLeastEngagedResidents"),
        this.$store.dispatch('getAdditionalServices'),
        this.$store.dispatch('getMaintenanceRequests'),
        this.$store.dispatch("getAdHocActivitiesDashboardReport", { fromDate: moment().add(-1, "months").toDate(), toDate: moment().toDate() })
      ]);

      this.loading = false;
    } catch (error) {
      console.error("Error during component initialization:", error);
    }
  },
  computed: {
    calenderEvents(): Calendar[] {
      return this.$store.state.calenderModule.calenderEvents.filter(x => x.isPublished === true);
    },
    familyLikes(): any[] {
      return this.$store.state.likesModule.familyLikes;
    },
    leastActivitiesRecorded(): any[] {
      return this.$store.state.adHocActivitiesModule.adHocActivitiesDashboardReport?.leastActivitiesRecorded
    },
    mostActivitiesRecorded(): any[] {
      return this.$store.state.adHocActivitiesModule.adHocActivitiesDashboardReport?.mostActivitiesRecorded
    },
    residents(): Resident[] {
      return this.$store.state.residentModule.residentsShortData || [];
    },
    residentsWithUpcomingBirthdays(): Resident[] {
      return this.$store.state.residentModule.residentsWithUpcomingBirthdays;
    },
    inProgressMaintenanceRequests() {
      const maintenanceRequests =
        this.$store.state.maintenanceModule.maintenanceRequests.filter(
          function (el) {
            return el.status == 'inprogress';
          }
        );
      return maintenanceRequests.length;
    },
    newMaintenanceRequests() {
      const maintenanceRequests =
        this.$store.state.maintenanceModule.maintenanceRequests.filter(
          function (el) {
            return el.status == 'new';
          }
        );
      return maintenanceRequests.length;
    },
    leastEngagedResidents(): Resident[] {
      return this.$store.state.residentModule.leastEngagedResidents;
    },
    serviceRequestsAwaiting(): ServiceRequest[] {
      return this.$store.state.additionalServicesModule.serviceRequestsDashboard;
    },
    calenderEventsLifestyle(): Calendar[] {

      const events = this.$store.getters.calenderEventsLifestyle
        .filter(event => event.isPublished === true && event.startDate >= this.sevenDaysAgo && event.endDate <= this.todayDate)
        .sort((a, b) => b.startDate - a.startDate)

      return events
    },
    calenderEventsPremium(): Calendar[] {
      return this.$store.getters.calenderEventsPremium.filter(x => x.isPublished === true);
    },
    calenderEventsUnattended(): Calendar[] {
      return this.$store.state.calenderModule.calendarEventsPast.filter(x => x.isPublished === true);
    },
    calenderEventsScheduledCall(): Calendar[] {
      return this.$store.getters.calenderEventsScheduledCall;
    },
    calenderEventsConcierge(): Calendar[] {
      return this.$store.getters.calenderEventsPremium.filter((event: Calendar) => this.additionalEventServices.find((service: NewAdditionalService) => service.id === event.invitees[0]))
    },
    notifications(): Notification[] {
      return this.$store.state.notificationsModule.notifications;
    },
    facility(): FacilitySettingsModel {
      return this.$store.state.facilityModule.selectedFacility;
    },
    premiumServiceEventTypes() {
      return PremiumServiceEventTypes;
    },
    additionalEventServices(): NewAdditionalService[] {
      return this.$store.state.additionalServicesModule.additionalServices.filter((x: NewAdditionalService) => [
        this.premiumServiceEventTypes.EventBased,
        this.premiumServiceEventTypes.ConciergeEvent,
        this.premiumServiceEventTypes.Event,
      ].includes(x.type));
    }
  },
  methods: {
    formattedDateTimeString(date: Date, addYears: boolean, addTime: boolean) {
      if (!this.facility) return ''

      return getFormattedDateTimeString(date, this.facility.dateFormat, addTime ? this.facility.timeFormat : null, addYears);
    },
    openService(row): void {
      this.$router.push("bookings-and-orders/" + row.id);
    },
    openCalendarEvent(eventId): void {
      if (!eventId) return

      this.$router.push("calendar/" + eventId);
    },
    openResident(row): void {
      const residentId = row.residentId ? row.residentId : (row.createdByResidentId || row.cancelledByResidentId || row.going[0] || row.invitees[0]);
      this.$router.push("residents/" + residentId);
    },
    handleClickResident(row): void {
      this.$router.push('/residents/' + row.id);
    },
    handleClickAdHocActivity(row): void {
      this.$router.push('/reports/adHocActivitiesReport/' + row.residentId);
    }
  }
}
